import { useEffect, useState } from "react";
import type { ShipmentLimits } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentLimits";
import { Configuration } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { ShipmentLimitsApi } from "@freightsimple/generated-dashboard-openapi-client/apis/ShipmentLimitsApi";
import { getConfig } from "@/Apis/getConfig";

/**
 * Fallback values that match the backend ShipmentLimits constants. Used until the API responds,
 * and as a last resort if the request fails. Keeping them in sync is verified by the backend
 * controller test against the same constants, and by the Cypress override test that proves the
 * frontend actually consumes the API response.
 */
export const DEFAULT_SHIPMENT_LIMITS: ShipmentLimits = {
  lineItem: {
    maxLengthInches: 636,
    maxWidthInches: 636,
    maxHeightInches: 102,
    minTotalVolumeCubicInches: 1728,
    minWeightPerHandlingUnitPounds: 20,
    maxHandlingUnits: 30,
    minDescriptionLength: 5,
    disallowedHandlingUnitTypes: ["Other", "Loose"],
  },
  shipment: {
    minWeightPounds: 60,
    maxWeightPounds: 48000,
    maxTotalVolumeCubicInches: 53 * 12 * 8 * 12 * 8 * 12,
    maxHandlingUnits: 30,
    maxLineItems: 21,
  },
  liftgate: {
    maxDimensionInches: 96,
    maxWeightPerHandlingUnitPounds: 2500,
    longThinException: {
      maxLengthInches: 144,
      maxSecondAxisInches: 24,
      maxWeightPerHandlingUnitPounds: 200,
    },
  },
  flatbed: {
    lengthTriggerInches: 600,
    widthTriggerInches: 96,
  },
  savedCommodity: {
    maxDescriptionLength: 100,
    maxSkuIdentifierLength: 100,
  },
  dangerousGoods: {
    maxUnNumberLength: 10,
    maxClassificationLength: 100,
    maxPackingGroupLength: 20,
    maxProperShippingNameLength: 200,
    maxTechnicalNameLength: 150,
    maxPackagingTypeLength: 100,
    maxNumberPackages: 99999,
    maxTdgResponsibleNameLength: 45,
    maxNetExplosiveQuantityKg: 21772,
    erapPlanNumberPattern: "^\\d-\\d{4}(-\\d{3})?$",
  },
  temperatureHandling: {
    // April–October, 1-indexed.
    protectFromFreezingDisallowedMonths: [4, 5, 6, 7, 8, 9, 10],
  },
  // Never enforced — this only drives the soft "are you sure?" warning beside
  // the weight field.
  advisory: {
    maxDensityPoundsPerCubicFoot: 250,
  },
  // Initialised to an empty list because the actual list is loaded from the API on app start.
  // Defaulting to [] is the safe choice: pre-API-response, we don't want to falsely flag a
  // description as prohibited; the dashboard's existing JSON fixture also still gates this on
  // its own as a belt-and-braces measure until the API value arrives.
  prohibitedCommodityDescriptions: [],
};

let cachedLimits: ShipmentLimits | undefined;
let inflightFetch: Promise<ShipmentLimits> | undefined;

/**
 * Fills in any group, or newest field in an existing group, the response didn't carry.
 *
 * Production deploys the frontend and the backend in parallel — both steps hang
 * off `all-gates-passed` in `pipeline.deploy.yml`, and the frontend waits on one
 * dependency fewer — so a customer can load a new bundle while an old pod is
 * still answering `/shipment-limits`. That answer is a perfectly good 200 that
 * simply lacks the newest group, and the generated decoder passes a missing
 * object straight through as `undefined` rather than rejecting, so the `.catch`
 * below never sees it and the gap is cached for the rest of the session.
 * Anything reading through that group then throws mid-form, and a reload is the
 * only way out. The same window opens on a backend rollback.
 *
 * This deliberately is not a spread over the defaults: the decoder always writes
 * the key, so `{ ...DEFAULT_SHIPMENT_LIMITS, ...response }` would overwrite the
 * default with `undefined` and change nothing.
 */
function withDefaultsForMissingGroups(
  response: ShipmentLimits,
): ShipmentLimits {
  return {
    ...response,
    shipment: {
      ...response.shipment,
      maxLineItems:
        response.shipment.maxLineItems ??
        DEFAULT_SHIPMENT_LIMITS.shipment.maxLineItems,
    },
    advisory: response.advisory ?? DEFAULT_SHIPMENT_LIMITS.advisory,
    // A field added to an existing group has the same gap. Without this one the
    // package count field would refuse every value.
    dangerousGoods: {
      ...response.dangerousGoods,
      maxNumberPackages:
        response.dangerousGoods.maxNumberPackages ??
        DEFAULT_SHIPMENT_LIMITS.dangerousGoods.maxNumberPackages,
    },
  };
}

function fetchShipmentLimits(): Promise<ShipmentLimits> {
  if (cachedLimits) return Promise.resolve(cachedLimits);
  if (inflightFetch) return inflightFetch;

  const api = new ShipmentLimitsApi(new Configuration(getConfig()));
  inflightFetch = api
    .getShipmentLimits()
    .then((response) => {
      cachedLimits = withDefaultsForMissingGroups(response);
      return cachedLimits;
    })
    .catch((error) => {
      console.error(
        "Failed to fetch shipment limits, falling back to defaults",
        error,
      );
      inflightFetch = undefined;
      return DEFAULT_SHIPMENT_LIMITS;
    });
  return inflightFetch;
}

export function useLineItemsShipmentLimits(): ShipmentLimits {
  const [limits, setLimits] = useState<ShipmentLimits>(
    cachedLimits ?? DEFAULT_SHIPMENT_LIMITS,
  );

  useEffect(() => {
    if (cachedLimits) {
      return;
    }
    let cancelled = false;
    fetchShipmentLimits().then((fetched) => {
      if (!cancelled) {
        setLimits(fetched);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return limits;
}

// Test seam: reset module-level cache between tests.
export function __resetShipmentLimitsCacheForTests() {
  cachedLimits = undefined;
  inflightFetch = undefined;
}
